/**
 * The paymentTo / partyType rule that decides what enters a balance saldo.
 * Frontend side — mirrors helpers/balanceTotals.helper.js.
 * Keep both in sync: the same rule has to hold on the screen, in the PDF and
 * in the aggregate, and those three compute their totals independently.
 *
 * The rule
 * --------
 * paymentTo === 'Inmobiliaria': the agency collects the income, so it
 *   holds money that isn't its own and owes the propietario
 *       saldo = alquiler − IRPF − comisiones − gastos      (a PAGAR)
 *
 * paymentTo === 'Cliente': the propietario collects the income
 *   directly. The agency never touches it, so there is nothing to net the
 *   costs against and the settlement runs the other way
 *       saldo = −(comisiones + gastos)                     (a COBRAR)
 *
 * Both are the same signed expression with the rent term present or absent —
 * the sign carries the direction of payment.
 */

/**
 * Resolves who receives the income for debts linked to either supported source.
 * Contract wins only as a defensive precedence rule; a debt normally belongs
 * to a contract or an asset sale, not both.
 */
export const resolveDebtPaymentTo = (debt) =>
	debt?.contract?.paymentTo ?? debt?.assetSale?.paymentTo ?? null;

/**
 * Whether a due's income is money the agency is actually holding.
 *
 * Tested against the EXCLUDING value on purpose: paymentTo is
 * `required: false`, and direct debts carry no source at all. Everything unset
 * keeps counting exactly as it did before this rule existed, instead of
 * silently dropping out of historical balances.
 */
export const dueIsCollected = (due) => due?.debtPaymentTo !== 'Cliente';

/**
 * Whether a bill is charged to the propietario. Bills marked 'inquilino' are
 * the tenant's to pay and must never reduce the landlord's settlement.
 * Unset (legacy) bills keep charging the owner, as they do today.
 */
export const billChargesOwner = (bill) => bill?.partyType !== 'inquilino';

/** Bills assigned to the tenant are collected by the agency and add to the settlement. */
export const billChargesTenant = (bill) => bill?.partyType === 'inquilino';

/**
 * The saldo is signed: positive means the agency owes the propietario,
 * negative means the propietario owes the agency. Deriving the wording from
 * the sign — rather than from paymentTo — is what lets an account mixing both
 * collection modes read correctly, and makes an 'Inmobiliaria' contract whose
 * costs exceed its rent come out right on its own.
 */
export const saldoLabel = (signed) => (signed >= 0
	? 'Saldo a pagar al propietario'
	: 'Saldo a cobrar al propietario');

/**
 * Summarises which collection mode(s) a set of income dues represents, so the
 * screen and the PDF can caption the total from one shared derivation instead
 * of reimplementing the rule twice.
 *
 * `expenseConcepts` is the caller's dynamic treatAsExpense set; it defaults to
 * the single hardcoded slug so existing callers keep their old behaviour.
 */
const DEFAULT_EXPENSE_CONCEPTS = new Set(['gasto-de-activo-fijo']);

export const describeBalanceMode = (dues = [], expenseConcepts = DEFAULT_EXPENSE_CONCEPTS) => {
	let collectedCount = 0;
	let directCount = 0;
	dues.forEach((due) => {
		// Expense-flagged concepts are a deduction, not income — they say nothing
		// about who collects the rent, so they must not steer the caption.
		if (expenseConcepts.has(due.debtConcept)) return;
		if (dueIsCollected(due)) collectedCount += 1;
		else directCount += 1;
	});
	let mode = 'none';
	if (collectedCount > 0 && directCount > 0) mode = 'mixed';
	else if (directCount > 0) mode = 'owner';
	else if (collectedCount > 0) mode = 'agency';
	return { mode, collectedCount, directCount };
};

/**
 * Short label naming which side receives the income, from its source paymentTo.
 * Deliberately just the label — the arithmetic is visible in the table rows
 * themselves and in the sign-derived total, so restating a formula here only
 * adds noise.
 */
export const buildBalanceCaption = (dues = [], expenseConcepts = DEFAULT_EXPENSE_CONCEPTS) => {
	const { mode } = describeBalanceMode(dues, expenseConcepts);
	if (mode === 'none') return null;
	if (mode === 'mixed') return 'Pago a inmobiliaria y a propietario';
	return mode === 'owner' ? 'Pago a propietario' : 'Pago a inmobiliaria';
};
